import type { OnboardingStep } from '@reelplicate/api-client/model';
import {
  getGetMeQueryKey,
  useGetMe,
  useUpdateOnboarding,
} from '@reelplicate/api-client/users';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';

/**
 * The signed-in user's saved onboarding step, and `advance` to save the next
 * one. Saving only moves forward (the API ignores earlier steps). The fresh
 * user goes straight into the /me cache, so guards see it at once.
 */
export function useOnboardingProgress() {
  const queryClient = useQueryClient();
  const me = useGetMe();
  const update = useUpdateOnboarding({
    mutation: {
      onSuccess: (user) => queryClient.setQueryData(getGetMeQueryKey(), user),
    },
  });

  /** Saves `step`; resolves false (after a toast) when saving failed. */
  async function advance(step: OnboardingStep) {
    try {
      await update.mutateAsync({ data: { step } });
      return true;
    } catch {
      toast.error('Couldn’t save your progress', {
        description: 'Check your connection and try again.',
      });
      return false;
    }
  }

  return { step: me.data?.onboardingStep, me, advance, saving: update.isPending };
}
